import { OrderSection } from '../order/order-section/order-section';
import { usePurchaseOrderApiV1 } from '../../clients/quipli-service/purchase-order/purchase-order-api-v1';
import { useEffect, useState } from 'react';
import { PurchaseOrderPaymentMethod } from '../../clients/quipli-service/purchase-order/purchase-order';
import { toast } from '../../util/toasts';
import SkeletonLoader from '../../components/skeleton-loader/skeleton-loader';
import { Field, FieldDescription, FieldLabel } from '../../components/field/field';
import { TextArea } from '../../components/textarea/textarea';
import { Button } from '../../components/form/button';
import { Spinner } from '../../components/spinner/spinner';
import { Radio } from '../../components/radio/radio';
import Icon from '../../components/icons/icon';
import classNames from 'classnames';

export const PurchasePaymentSelect = ({
  isEditing = false,
  selectedPaymentMethod,
  onSelect,
  className,
}: {
  isEditing?: boolean;
  selectedPaymentMethod?: PurchaseOrderPaymentMethod;
  onSelect: (pm: PurchaseOrderPaymentMethod) => void;
  className?: string;
}) => {
  const { listPaymentMethods } = usePurchaseOrderApiV1();

  const [paymentMethods, setPaymentMethods] = useState<PurchaseOrderPaymentMethod[]>();
  const [editPaymentMethod, setEditPaymentMethod] = useState<PurchaseOrderPaymentMethod>();
  const [showForm, setShowForm] = useState<boolean>(false);

  const fetchPaymentMethods = () => {
    listPaymentMethods()
      .then(setPaymentMethods)
      .catch((err: any) => toast(err.message, 'error'));
  };

  useEffect(() => {
    isEditing && fetchPaymentMethods();
  }, [isEditing]);

  return (
    <OrderSection className={classNames(className)} header="Payment">
      <div className="pt-4 flex flex-col">
        {isEditing ? (
          paymentMethods ? (
            <>
              {!showForm ? (
                <>
                  {paymentMethods.map((paymentMethod) => (
                    <div key={paymentMethod.id} className="flex items-center justify-between gap-4">
                      <Radio
                        name="paymentMethod"
                        value={paymentMethod.id}
                        label={paymentMethod.name}
                        isChecked={selectedPaymentMethod?.id === paymentMethod.id}
                        onChange={() => onSelect(paymentMethod)}
                      />

                      <div className="flex items-center">
                        <Button
                          className="icon-button"
                          onClick={() => {
                            setEditPaymentMethod(paymentMethod);
                            setShowForm(true);
                          }}
                        >
                          <Icon icon="Edit" size="medium" stroke="var(--grey-400)" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  <div className="mt-4">
                    <Button className="button-primary" onClick={() => setShowForm(true)}>
                      Add Payment Method
                    </Button>
                  </div>
                </>
              ) : (
                <PaymentMethodForm
                  paymentMethod={editPaymentMethod}
                  onSubmit={(pm) => {
                    fetchPaymentMethods();
                    setEditPaymentMethod(undefined);
                    setShowForm(false);
                    onSelect(pm);
                  }}
                  onCancel={() => {
                    setEditPaymentMethod(undefined);
                    setShowForm(false);
                  }}
                />
              )}
            </>
          ) : (
            <Loader />
          )
        ) : (
          <div>
            <FieldLabel label={selectedPaymentMethod?.name || 'No payment method selected'} />
            <FieldDescription description={selectedPaymentMethod?.description} />
          </div>
        )}
      </div>
    </OrderSection>
  );
};

const PaymentMethodForm = ({
  paymentMethod,
  onSubmit,
  onCancel,
}: {
  paymentMethod?: PurchaseOrderPaymentMethod;
  onSubmit: (pm: PurchaseOrderPaymentMethod) => void;
  onCancel: () => void;
}) => {
  const { createPaymentMethod, updatePaymentMethod } = usePurchaseOrderApiV1();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [request, setRequest] = useState<Pick<PurchaseOrderPaymentMethod, 'name' | 'description'>>({
    name: paymentMethod?.name || '',
    description: paymentMethod?.description || '',
  });

  const isUpdate = !!paymentMethod && !!paymentMethod.id;
  const isDisabled = !request.name;

  const handleSubmit = () => {
    if (isDisabled || isSubmitting) return;

    const fn = () =>
      isUpdate
        ? updatePaymentMethod(paymentMethod.id, request.name, request.description)
        : createPaymentMethod(request.name, request.description);

    setIsSubmitting(true);
    fn()
      .then((pm) => {
        toast(`Payment method ${isUpdate ? 'updated' : 'created'}`, 'success');
        onSubmit(pm);
      })
      .catch((err: any) => toast(err.message, 'error'))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className="flex flex-col gap-4">
      <Field
        type="text"
        label="Name"
        labelType="text-xs"
        value={request.name}
        onChange={(v) => setRequest({ ...paymentMethod, name: v })}
      />

      <TextArea
        label="Description"
        labelType="text-xs"
        value={request.description}
        onChange={({ target }) => setRequest({ ...request, description: target.value })}
      />

      <div className="flex gap-4">
        <Button className="button-secondary" disabled={isSubmitting} onClick={() => !isSubmitting && onCancel()}>
          Cancel
        </Button>
        <Button className="button-primary" disabled={isDisabled} onClick={handleSubmit}>
          {isSubmitting && <Spinner />}
          {isUpdate ? 'Update' : 'Create'}
        </Button>
      </div>
    </div>
  );
};

const Loader = () => {
  return <SkeletonLoader num={1} style={{ height: '30px', width: '100%' }} />;
};
